import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { mergeDocumentMatchRows } from './documentMatchRows';
import LoadingSpinner from "src/components/general/js/spinnerLoading";
import MatchRow from "./MatchRow";
import StrongCandidate from "./StrongCandidate";
import WeakCandidate from "./WeakCandidate";
import MatchRecommenderCandidate from "./MatchRecommenderCandidate";
import { GenerateMatch } from "../../../../../../graphql/conciliacion";
import { dt } from "../../../../../../utils/documentsI18n";
import { ObligationsContext } from "src/context";
import { setObligationCatalog } from "./matchRowHelpers";

const documentLookupId = (doc) =>
  doc?.id ?? doc?._id ?? doc?.docId ?? doc?.settleId ?? null;

/**
 * El query allMatchsByTransactionId devuelve documentos únicos ($in + cache GraphQL).
 * Si la transacción matcheó el mismo docId más de una vez, hay que repetir la fila
 * una vez por cada ítem de transaction.matchs (identidad = matchId).
 */
const expandEntitiesByMatchRows = (entities, matchRows, getMatchEntityId) => {
  if (!Array.isArray(entities) || entities.length === 0) return entities;
  if (!Array.isArray(matchRows) || matchRows.length === 0) return entities;

  const byId = new Map();
  for (const entity of entities) {
    const id = documentLookupId(entity);
    if (id != null && id !== "") byId.set(String(id), entity);
  }

  const expanded = [];
  const consumed = new Set();
  for (const m of matchRows) {
    const entityId = getMatchEntityId(m);
    if (entityId == null || entityId === "") continue;
    const entity = byId.get(String(entityId));
    if (!entity) continue;
    consumed.add(String(entityId));
    expanded.push({
      ...entity,
      matchId: m.matchId,
      matchedAmount: m.matchedAmount ?? m.amount,
      localValue: m.localValue,
      localAmount: m.localAmount,
      matchCurrency: m.matchCurrency,
      matchSource: m.matchSource,
      originalAmount: m.originalAmount ?? entity.originalAmount,
      exchangeRate: m.exchangeRate,
      userRelated: m.userRelated ?? entity.userRelated,
      entries: m.entries ?? entity.entries,
    });
  }

  for (const entity of entities) {
    const id = documentLookupId(entity);
    if (id != null && id !== "" && !consumed.has(String(id))) {
      expanded.push(entity);
    }
  }
  return expanded;
};

const expandDocumentsByTransactionMatchs = (docs, transactionMatchs) =>
  expandEntitiesByMatchRows(
    docs,
    transactionMatchs,
    (m) => m?.docId ?? m?.id ?? m?.settleId,
  );

const expandTransactionsByDocumentMatchs = (txs, documentMatchs) =>
  expandEntitiesByMatchRows(
    txs,
    documentMatchs,
    (m) => m?.id ?? m?.docId ?? m?.settleId,
  );

/**
 * MatchList - Container component for rendering all matches
 * Handles loading states and empty states
 * Adds fade-in animation for new matches
 * 
 * Display Logic:
 * - If transaction has matches, show all matches
 * - If remaining > 0 and candidates exist:
 *   - Show strong candidates if they exist
 *   - Otherwise show weak candidates if they exist
 *   - If no candidates, only show matches
 * - If no matches and no candidates, show empty message
 */
const MatchList = ({ 
  matches, 
  matchContainer, 
  editData, 
  isTransitioning, 
  fetching, 
  history, 
  location,
  strongCandidates = [],
  weakCandidates = [],
  matchRecommendations = [],
  onMatchRecommendationRejected,
  onMatchRecommendationApplied,
  readOnly = false
}) => {
  const [newMatchIds, setNewMatchIds] = useState(new Set());
  const previousMatchIdsRef = useRef(new Set());

  // Catálogo canónico de obligaciones (ObligationsContext). Consumirlo aquí re-renderiza las filas
  // cuando llega, y sincroniza el resolver de labels (matchRowHelpers) con la fuente del backend.
  const { obligations: obligationCatalog } = useContext(ObligationsContext) ?? {};
  useEffect(() => {
    setObligationCatalog(obligationCatalog);
  }, [obligationCatalog]);
  
  // Shared GenerateMatch hook for all weak candidates to share loading state
  const { executeMutation: generateMatch, fetching: generateMatchFetching } = GenerateMatch();

  // Memoize combined matches to prevent unnecessary recalculations
  // Filter out documents with isHidden: true
  const allMatches = useMemo(() => {
    if (
      matches?.allMatchsByTransactionId?.length > 0 ||
      matches?.allMatchManualByTransactionId?.length > 0 ||
      matches?.allMatchByClay?.length > 0 ||
      matches?.allTransactionsByDocumentId?.length > 0
    ) {
      const combined = [
        ...(matches.allMatchsByTransactionId || []),
        ...(matches.allMatchByClay || []),
        ...(matches.allMatchManualByTransactionId || []),
        ...(matches.allTransactionsByDocumentId || []),
      ];
      // Filter out matches with isHidden: true
      return combined.filter(match => {
        return !(match.isHidden === true || match.meta?.isHidden === true);
      });
    }
    return null;
  }, [
    matches?.allMatchsByTransactionId,
    matches?.allMatchManualByTransactionId,
    matches?.allMatchByClay,
    matches?.allTransactionsByDocumentId,
  ]);

  const isEditDataDocument = !!(editData?.documentType || editData?.meta?.documentType);

  const displayMatches = useMemo(() => {
    if (!allMatches) return null;
    if (isEditDataDocument) {
      const rows = mergeDocumentMatchRows(
        editData?.meta?.matchs?.inMatch,
        editData?.meta?.matchs?.outMatch,
      );
      return expandTransactionsByDocumentMatchs(allMatches, rows);
    }
    return expandDocumentsByTransactionMatchs(allMatches, editData?.matchs);
  }, [allMatches, isEditDataDocument, editData?.matchs, editData?.meta?.matchs]);

  // IDs activos en editData (fuente de verdad tras match/unmatch; evita matches obsoletos del query)
  const activeMatchIds = useMemo(() => {
    const ids = new Set();
    const addId = (value) => {
      if (value != null && value !== '') ids.add(String(value));
    };
    if (isEditDataDocument && editData?.meta?.matchs) {
      for (const m of editData.meta.matchs.inMatch || []) {
        addId(m.id);
        addId(m.docId);
        addId(m.settleId);
      }
      for (const m of editData.meta.matchs.outMatch || []) {
        addId(m.id);
        addId(m.docId);
        addId(m.settleId);
      }
    } else if (Array.isArray(editData?.matchs)) {
      for (const m of editData.matchs) {
        addId(m.docId);
        addId(m.id);
        addId(m.settleId);
      }
    }
    return ids;
  }, [editData, isEditDataDocument]);

  // Extract inMatch and outMatch IDs from editData (only for documents)
  // inMatch: documents/transactions that this document paid FOR
  // outMatch: documents/transactions that paid FOR this document
  const { inMatchIds, outMatchIds } = useMemo(() => {
    if (isEditDataDocument && editData?.meta?.matchs) {
      const inMatchIdSet = new Set(
        (editData.meta.matchs.inMatch || [])
          .map(match => match.id || match.docId || match.settleId)
          .filter(Boolean)
          .map(String)
      );
      const outMatchIdSet = new Set(
        (editData.meta.matchs.outMatch || [])
          .map(match => match.id || match.docId || match.settleId)
          .filter(Boolean)
          .map(String)
      );
      return { inMatchIds: inMatchIdSet, outMatchIds: outMatchIdSet };
    }
    return { inMatchIds: new Set(), outMatchIds: new Set() };
  }, [editData, isEditDataDocument]);

  // Separate matches into inMatch and outMatch explicitly
  const { inMatches, outMatches } = useMemo(() => {
    if (!displayMatches || displayMatches.length === 0) {
      return { inMatches: [], outMatches: [] };
    }

    const inMatchesList = [];
    const outMatchesList = [];

    displayMatches.forEach(match => {
      const matchId = match.id || match.docId || match.settleId || match._id;
      if (!matchId) return;

      const matchIdStr = String(matchId);
      // Solo mostrar matches que siguen en editData (query del servidor puede ir desfasado tras unmatch)
      const isActive =
        activeMatchIds.has(matchIdStr) ||
        (match._id != null && activeMatchIds.has(String(match._id)));
      if (!isActive) return;

      // Si un match está en inMatch Y outMatch (match mutuo — p.ej. la Comisión Pasarela o la Rendición de Anticipo
      // de Garantía contra una Garantía de Clientes), se muestra en "Fue pagado con" (inMatch), NO en "Está pagando a"
      // (outMatch): inMatch tiene prioridad. Así una garantía saldada por banco + comisión + rendición muestra las 3
      // bajo "Fue pagado con" y nada bajo "Está pagando a".
      if (inMatchIds.has(matchIdStr)) {
        inMatchesList.push(match);
      } else if (outMatchIds.has(matchIdStr)) {
        outMatchesList.push(match);
      } else if (!isEditDataDocument) {
        // Transacción: filas en transaction.matchs van a inMatch
        inMatchesList.push(match);
      }
    });

    return { inMatches: inMatchesList, outMatches: outMatchesList };
  }, [displayMatches, inMatchIds, outMatchIds, activeMatchIds, isEditDataDocument]);

  // Filter out candidates that are fully reconciled (remaining === 0) and those explicitly hidden.
  // Important: candidates can arrive without `meta.remaining` (e.g. while documents are still loading),
  // so we must NOT filter them out just because remaining is undefined.
  // Tipos de obligación que no deben aparecer como candidatos de match
  const BANNED_OBLIGATION_TYPES = ['proyeccion', 'estimaciondeegreso', 'estimaciondeingreso'];

  const filteredStrongCandidates = useMemo(() => {
    return (strongCandidates || []).filter(candidate => {
      const candidateRemaining = candidate?.meta?.remaining ?? candidate?.remaining ?? candidate?.meta?.amount ?? candidate?.amount;
      const isHidden = candidate.isHidden === true || candidate.meta?.isHidden === true;
      const isBannedType = BANNED_OBLIGATION_TYPES.includes(candidate?.meta?.type);
      return candidateRemaining !== 0 && !isHidden && !isBannedType;
    });
  }, [strongCandidates]);

  // Track IDs used by strong candidates to avoid duplicates
  const strongCandidateIds = useMemo(() => {
    return new Set(filteredStrongCandidates.map(c => c?.id || c?.docId).filter(Boolean));
  }, [filteredStrongCandidates]);

  // Filter weak candidates and exclude those already in strong and banned types
  const filteredWeakCandidates = useMemo(() => {
    return (weakCandidates || []).filter(candidate => {
      const candidateId = candidate?.id || candidate?.docId;
      const candidateRemaining = candidate?.meta?.remaining ?? candidate?.remaining ?? candidate?.meta?.amount ?? candidate?.amount;
      const isHidden = candidate.isHidden === true || candidate.meta?.isHidden === true;
      const isDuplicate = candidateId && strongCandidateIds.has(candidateId);
      const isBannedType = BANNED_OBLIGATION_TYPES.includes(candidate?.meta?.type);
      return candidateRemaining !== 0 && !isHidden && !isDuplicate && !isBannedType;
    });
  }, [weakCandidates, strongCandidateIds]);

  // NOTA: aiRecommendations y systemRecommendations ya no se usan
  // Todo viene ahora de matchRecommendations (Zona de Entrenamiento)

  // Mostrar todas las recomendaciones del Match Recommender pendientes (mismo criterio que el contador del badge)
  const filteredMatchRecommendations = useMemo(() => {
    const list = matchRecommendations || [];
    return list.filter(rec => {
      const status = rec?.status;
      return status === 'pending' || status === 'PENDING';
    });
  }, [matchRecommendations]);

  // Detect new matches when matches change
  useEffect(() => {
    // Combine both outMatches and inMatches for detection
    const combinedMatches = [...(outMatches || []), ...(inMatches || [])];
    
    if (!combinedMatches || combinedMatches.length === 0) {
      previousMatchIdsRef.current = new Set();
      setNewMatchIds(new Set());
      return;
    }

    // Get current match IDs as a Set
    const currentMatchIds = new Set(
      combinedMatches.map(match => 
        match.matchId || match.id || match.docId || match.settleId || `${match.documentDate || match.date}-${match.amount}`
      )
    );

    // Only process if there are previous matches (skip on initial load)
    if (previousMatchIdsRef.current.size > 0) {
      // Find new matches (exist in current but not in previous)
      const newIds = Array.from(currentMatchIds).filter(id => !previousMatchIdsRef.current.has(id));

      if (newIds.length > 0) {
        setNewMatchIds(new Set(newIds));
        // Remove fade-in class after animation completes (600ms)
        const timer = setTimeout(() => {
          setNewMatchIds(new Set());
        }, 600);
        
        // Update previous matches reference AFTER setting new matches
        previousMatchIdsRef.current = currentMatchIds;
        
        return () => clearTimeout(timer);
      }
    }

    // Update previous matches reference on every change
    previousMatchIdsRef.current = currentMatchIds;
  }, [outMatches, inMatches]);

  // Show loading if transitioning or fetching without data
  if ((!matches && fetching) || isTransitioning) {
    return (
      <div className="no-comments">
        <LoadingSpinner />
      </div>
    );
  }

  // Calculate remaining amount - check both direct fields and meta fields
  // For documents, remaining is stored in meta.remaining; for transactions, directly in remaining.
  const remaining = editData?.remaining !== undefined && editData?.remaining !== null
    ? editData.remaining
    : (editData?.meta?.remaining !== undefined && editData?.meta?.remaining !== null
        ? editData.meta.remaining
        : null);
  // null/undefined means value not yet loaded → cannot confirm it's 0, so show candidates.
  const remainingUnknown = remaining === null || remaining === undefined;

  // Check if we have matches (inMatch or outMatch)
  const hasMatches = (inMatches && inMatches.length > 0) || (outMatches && outMatches.length > 0);
  const hasInMatches = inMatches && inMatches.length > 0;
  const hasOutMatches = outMatches && outMatches.length > 0;
  
  // Check if we have candidates (using filtered versions)
  const hasStrongCandidates = filteredStrongCandidates && filteredStrongCandidates.length > 0;
  const hasWeakCandidates = filteredWeakCandidates && filteredWeakCandidates.length > 0;
  const hasMatchRecommendations = filteredMatchRecommendations && filteredMatchRecommendations.length > 0;
  const hasAnyCandidates = hasStrongCandidates || hasWeakCandidates || hasMatchRecommendations;
  
  // If no matches and no candidates, show empty state
  if (!hasMatches && !hasAnyCandidates) {
    // Check if editData is a document (has documentType) or transaction
    const isDocument = editData?.documentType || editData?.meta?.documentType;
    const emptyMessage = isDocument
      ? dt('reconcile.emptyDocument')
      : dt('reconcile.emptyTransaction');
    
    return (
      <div className="no-comments">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Mostrar TODAS las recomendaciones de match (strong, weak, match recommender)
  // Cada tipo se muestra cuando exista y remaining > 0 - sin exclusividad mutua
  const showStrongCandidates     = (remainingUnknown || remaining > 0) && hasStrongCandidates;
  const showWeakCandidates       = (remainingUnknown || remaining > 0) && hasWeakCandidates;
  const showMatchRecommendations = (remainingUnknown || remaining > 0) && hasMatchRecommendations;

  return (
    <div className="comments-list">
      {/* Matches first: reconciled items above candidates */}
      {/* OutMatch - Documents/transactions that this document pays */}
      {hasOutMatches && (
        <>
          <div style={{ textAlign: 'left', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#1f2d3a' }}>
            Está pagando a:
          </div>
          <div className={`match-group ${outMatches.length >= 2 ? 'has-matches' : ''}`}>
            {outMatches.map((match, index) => {
              const matchId = match.matchId || match.id || match.docId || match.settleId || `${match.documentDate || match.date}-${match.amount}`;
              const isNewMatch = newMatchIds.has(matchId);

              return (
                <MatchRow
                  key={`outmatch-${matchId}-${index}`}
                  match={match}
                  index={index}
                  matchContainer={matchContainer}
                  editData={editData}
                  history={history}
                  location={location}
                  isNewMatch={isNewMatch}
                  readOnly={readOnly}
                  transactionAmount={editData?.amount}
                />
              );
            })}
          </div>
          {hasInMatches && (
            <div style={{ height: '8px' }} />
          )}
        </>
      )}

      {/* InMatch - Payment source for this document */}
      {hasInMatches && (
        <>
          {hasOutMatches && (
            <div style={{ textAlign: 'left', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#1f2d3a' }}>
              {dt('reconcile.reconciledWith')}
            </div>
          )}
          <div className={`match-group ${inMatches.length >= 2 ? 'has-matches' : ''}`}>
            {inMatches.map((match, index) => {
              const matchId = match.matchId || match.id || match.docId || match.settleId || `${match.documentDate || match.date}-${match.amount}`;
              const isNewMatch = newMatchIds.has(matchId);

              return (
                <MatchRow
                  key={`inmatch-${matchId}-${index}`}
                  match={match}
                  index={index}
                  matchContainer={matchContainer}
                  editData={editData}
                  history={history}
                  location={location}
                  isNewMatch={isNewMatch}
                  readOnly={readOnly}
                  transactionAmount={editData?.amount}
                />
              );
            })}
          </div>
        </>
      )}

      {hasMatches && hasAnyCandidates && (
        <div style={{ height: '8px' }} />
      )}

      {/* Strong Match Candidates - Only show if remaining > 0 and strong candidates exist */}
      {showStrongCandidates && (
        <>
          <div style={{ textAlign: 'left', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#1f2d3a' }}>
            {dt('reconcile.strongCandidateTitle')}
          </div>
          {filteredStrongCandidates.map((candidate, index) => (
            <StrongCandidate
              key={`strong-candidate-${candidate.id || candidate.docId}-${index}`}
              candidate={candidate}
              index={index}
              totalStrongCandidates={filteredStrongCandidates.length}
              matchContainer={matchContainer}
              editData={editData}
              history={history}
              location={location}
              generateMatch={generateMatch}
              generateMatchFetching={generateMatchFetching}
              readOnly={readOnly}
            />
          ))}
        </>
      )}
      
      {/* Weak Match Candidates - Only show if remaining > 0, no strong candidates, but weak candidates exist */}
      {showWeakCandidates && (
        <>
          <div style={{ textAlign: 'left', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#1f2d3a' }}>
            {filteredWeakCandidates.length > 1
              ? dt('reconcile.possibleMatchesMany')
              : dt('reconcile.possibleMatchOne')}
          </div>
          {filteredWeakCandidates.map((candidate, index) => (
            <WeakCandidate
              key={`weak-candidate-${candidate.id || candidate.docId}-${index}`}
              candidate={candidate}
              index={index}
              matchContainer={matchContainer}
              editData={editData}
              history={history}
              location={location}
              generateMatch={generateMatch}
              generateMatchFetching={generateMatchFetching}
              readOnly={readOnly}
            />
          ))}
        </>
      )}

      {/* Match Recommender: recomendaciones de IA desde la Zona de Entrenamiento */}
      {showMatchRecommendations && filteredMatchRecommendations.map((recommendation, index) => (
        <MatchRecommenderCandidate
          key={`match-recommendation-${recommendation?.id ?? recommendation?._id ?? index}-${index}`}
          recommendation={recommendation}
          index={index}
          editData={editData}
          history={history}
          location={location}
          onRejected={onMatchRecommendationRejected}
          onApplied={onMatchRecommendationApplied}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
};

export default MatchList;

